import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { filter, map, take } from 'rxjs';

import { UserStateService } from '../../features/user/services';

/**
 * Route guard that checks if the logged-in user has the required permission.
 *
 * Waits for `authReady()` before evaluating permissions - on a fresh bootstrap (hard
 * refresh into a deep route) the "who am I" rehydration call that populates
 * `permissions()` is still async, so checking immediately can race it and wrongly
 * reject a user who does have the permission.
 *
 * Usage in routes:
 *   canActivate: [authGuard, permissionGuard('vt-read-order')]
 *   // or using the PERMISSIONS constant:
 *   canActivate: [authGuard, permissionGuard(PERMISSIONS.Orders.Read)]
 */
export const permissionGuard = (requiredPermission: string): CanActivateFn => {
  return () => {
    const userStateService = inject(UserStateService);
    const router = inject(Router);

    return toObservable(userStateService.authReady).pipe(
      filter((ready) => ready),
      take(1),
      map(() => {
        if (!userStateService.isLoggedIn()) {
          return true;
        }

        if (userStateService.hasPermission(requiredPermission)) {
          return true;
        }

        return router.createUrlTree(['/']);
      }),
    );
  };
};

/**
 * Route guard that passes if the logged-in user has AT LEAST ONE of the given permissions.
 * Use this instead of `permissionGuard` when a route should be reachable by any one of several
 * distinct permissions (e.g. the /admin tree, reachable with either Users.Read or Roles.Read).
 * Row-level scoping (e.g. a Vendedor only seeing their own assigned customers/orders) is driven
 * by IUserContext.CanSeeAllData on the backend, not by a separate permission name.
 *
 * Usage in routes:
 *   canActivate: [authGuard, permissionsGuard([PERMISSIONS.Users.Read, PERMISSIONS.Roles.Read])]
 */
export const permissionsGuard = (
  requiredPermissions: string[],
): CanActivateFn => {
  return () => {
    const userStateService = inject(UserStateService);
    const router = inject(Router);

    return toObservable(userStateService.authReady).pipe(
      filter((ready) => ready),
      take(1),
      map(() => {
        if (!userStateService.isLoggedIn()) {
          return true;
        }

        if (userStateService.hasPermissions(requiredPermissions)) {
          return true;
        }

        return router.createUrlTree(['/']);
      }),
    );
  };
};

/**
 * Route guard for the platform billing admin - checks `userData().isAdmin` (backed by the
 * server-side AdminEmailGate) instead of a permission string. Billing administration must
 * never be reachable by granting a permission to a role.
 *
 * Usage in routes:
 *   canActivate: [authGuard, adminOnlyGuard]
 */
export const adminOnlyGuard: CanActivateFn = () => {
  const userStateService = inject(UserStateService);
  const router = inject(Router);

  return toObservable(userStateService.authReady).pipe(
    filter((ready) => ready),
    take(1),
    map(() => {
      if (!userStateService.isLoggedIn()) {
        return true;
      }

      if (userStateService.userData()?.isAdmin) {
        return true;
      }

      return router.createUrlTree(['/']);
    }),
  );
};
